/**
 * OperatorMultiSelect — Searchable multi-select dropdown for operator
 * assignments (Pratiche / Lavoro Quotidiano / Diario / Scadenze).
 *
 * Spec (20/02/2026):
 *   - searchable list (input filter on fullName)
 *   - checkbox selection inside the popover
 *   - removable chips below the trigger
 *   - compact, responsive layout
 *   - backward-compatible value:
 *       value = string | string[] | null | undefined
 *       onChange always called with string[] (caller is free to map to
 *       legacy single field via [0]).
 *
 * Compatibility with backend:
 *   The backend normalizes legacy `operatorId` ↔ `operatorIds[]` on
 *   every write. Callers should send BOTH so older operators reading
 *   on the legacy single-string code path keep working.
 */
import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * @param {Object}   props
 * @param {Array}    props.operators        - Full operator list ({id, fullName, role, status, ...})
 * @param {string|string[]|null} props.value
 * @param {Function} props.onChange         - (string[]) => void
 * @param {string}   [props.placeholder]
 * @param {string}   [props.emptyHint]
 * @param {boolean}  [props.disabled]
 * @param {string}   [props.dataTestid]     - root testid prefix
 * @param {boolean}  [props.compact]        - smaller trigger
 */
export function OperatorMultiSelect({
  operators,
  value,
  onChange,
  placeholder = "Seleziona operatori…",
  emptyHint = "Nessun operatore disponibile",
  disabled = false,
  dataTestid = "operator-multi-select",
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  // Normalize value → array always.
  const selectedIds = useMemo(() => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string" && value) return [value];
    return [];
  }, [value]);

  const ops = Array.isArray(operators) ? operators : [];
  const filteredOps = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ops;
    return ops.filter((o) =>
      (o.fullName || "").toLowerCase().includes(s)
      || (o.role || "").toLowerCase().includes(s)
    );
  }, [ops, q]);

  const selectedOps = useMemo(
    () => selectedIds.map((id) => ops.find((o) => o.id === id)).filter(Boolean),
    [selectedIds, ops]
  );

  const toggle = (id) => {
    if (disabled) return;
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const remove = (id) => {
    if (disabled) return;
    onChange(selectedIds.filter((x) => x !== id));
  };

  const clear = () => {
    if (disabled) return;
    onChange([]);
  };

  return (
    <div className="space-y-1.5" data-testid={dataTestid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              compact ? "h-8 text-xs" : "h-9 text-sm",
              selectedIds.length === 0 && "text-muted-foreground"
            )}
            data-testid={`${dataTestid}-trigger`}
            aria-expanded={open}
          >
            <span className="truncate">
              {selectedIds.length === 0
                ? placeholder
                : `${selectedIds.length} operator${selectedIds.length === 1 ? "e" : "i"} selezionat${selectedIds.length === 1 ? "o" : "i"}`}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Cerca operatore…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-8 pl-7 text-xs"
                data-testid={`${dataTestid}-search`}
              />
            </div>
          </div>
          <div className="max-h-[280px] overflow-y-auto py-1">
            {filteredOps.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                {q ? "Nessun operatore corrisponde alla ricerca" : emptyHint}
              </p>
            ) : (
              filteredOps.map((op) => {
                const isChecked = selectedIds.includes(op.id);
                return (
                  <button
                    key={op.id}
                    type="button"
                    onClick={() => toggle(op.id)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 transition-colors",
                      isChecked && "bg-blue-50/40"
                    )}
                    data-testid={`${dataTestid}-option-${op.id}`}
                  >
                    <span className={cn(
                      "h-4 w-4 shrink-0 rounded border flex items-center justify-center",
                      isChecked ? "bg-blue-600 border-blue-600" : "border-slate-300 bg-white"
                    )}>
                      {isChecked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 truncate">{op.fullName || "—"}</span>
                    {op.role && (
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                        {op.role.replace("operatore_", "")}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
          {selectedIds.length > 0 && (
            <div className="border-t p-2 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{selectedIds.length} selezionati</span>
              <button
                type="button"
                onClick={clear}
                className="text-blue-600 hover:underline"
                data-testid={`${dataTestid}-clear`}
              >
                Pulisci tutto
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Chips */}
      {selectedOps.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`${dataTestid}-chips`}>
          {selectedOps.map((op) => (
            <Badge
              key={op.id}
              variant="secondary"
              className="text-xs gap-1 pl-2 pr-1 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100"
              data-testid={`${dataTestid}-chip-${op.id}`}
            >
              {op.fullName || op.id}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(op.id)}
                  className="ml-0.5 rounded-full hover:bg-blue-200 p-0.5 transition-colors"
                  aria-label={`Rimuovi ${op.fullName}`}
                  data-testid={`${dataTestid}-chip-remove-${op.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default OperatorMultiSelect;
